'use client';

import { useState } from 'react';
import dayjs from 'dayjs';
import Image from 'next/image';
import { lang, hocTranslateServerClient } from '@/helpers/translate';
import { H1, Kicker, kicker } from '@/core/editorial';

function Blog({ t, posts, locale }) {
  const currentLang = locale || lang();

  const [firstFive] = useState((posts || []).slice(0, 5));
  const [remainePosts] = useState((posts || []).slice(5));

  const renderFirst = ({
    title, slug, postcategory, publishedAt, mainImage,
  }) => (
    <a href={`/${currentLang}/blog/${slug?.current}`} className="group flex flex-col overflow-hidden rounded-xl border border-was-ink/8 bg-white transition duration-300 hover:-translate-y-0.5 hover:border-was-ink/15">
      <div className="relative aspect-[16/10] w-full bg-was-ink/5">
        {mainImage?.asset?.url && (
        <Image
          src={mainImage?.asset?.url}
          alt={mainImage?.alt}
          fill
          className="object-cover"
        />
        ) }
      </div>
      <div className="flex flex-1 flex-col gap-3 p-6">
        <span className="text-[17px] font-bold leading-snug tracking-[-0.01em] text-was-ink">
          {title}
        </span>
        <div className="mt-auto flex items-center justify-between text-[13px] text-was-muted">
          <span>{t(`blog:category.${postcategory}`)}</span>
          <span>{dayjs(publishedAt).format('DD MMM YYYY')}</span>
        </div>
      </div>
    </a>
  );

  const renderRemain = ({
    title, slug, postcategory, publishedAt,
  }) => (
    <li className="border-b border-was-ink/8">
      <a className="group flex flex-col gap-2 py-5 text-[15px] transition-colors md:flex-row md:items-center md:justify-between md:gap-6" href={`/${currentLang}/blog/${slug?.current}`}>
        <span className="font-medium text-was-ink group-hover:text-was-violet">{title}</span>
        <div className="flex shrink-0 items-center gap-x-6 text-[13px] text-was-muted">
          <span>{dayjs(publishedAt).format('DD MMM YYYY')}</span>
          <span>{t(`blog:category.${postcategory}`)}</span>
        </div>
      </a>
    </li>
  );

  return (
    <div className="bg-white font-display text-was-ink">
      <section className="bg-white">
        <div className="mx-auto max-w-[1240px] px-6 pb-12 pt-28 lg:px-8 lg:pb-16 lg:pt-36">
          <Kicker n="01">{kicker(currentLang, 'blog', 0)}</Kicker>
          <h1 className={`mt-5 max-w-[820px] text-balance ${H1}`}>{t('blog:title')}</h1>
          <p className="mt-6 max-w-[620px] text-lg leading-relaxed text-was-slate">{t('blog:desc')}</p>
        </div>
        <div className="mx-auto max-w-[1240px] px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {firstFive.slice(0, 2).map((ff) => renderFirst(ff))}
          </div>
          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
            {firstFive.slice(2, 5).map((ff) => renderFirst(ff))}
          </div>
        </div>
      </section>
      {remainePosts?.length > 0 && (
        <section className="bg-white">
          <div className="mx-auto max-w-[1240px] px-6 py-20 lg:px-8 lg:py-28">
            <Kicker>{t('blog:more')}</Kicker>
            <ul className="m-0 mt-6 list-none border-t border-was-ink/8 p-0">
              {remainePosts.map((rp) => renderRemain(rp))}
            </ul>
          </div>
        </section>
      )}
    </div>
  );
}

export default hocTranslateServerClient(Blog);
